/*
 * Project: bellcloud
 * File: apps/frontend/src/features/guide/guideSteps.ts
 * Description: 페이지별 가이드 step 정의. target 은 항상 data-tour 속성을 기준으로 한다.
 *              문구는 i18n `guide` 네임스페이스 키로 관리한다.
 */

import type { GuideKey, GuideStep, GuideTranslate } from './types';

interface StepDef {
  /** data-tour 속성 셀렉터 */
  target: string;
  /** guide 네임스페이스 번역 키 */
  contentKey: string;
  /** 툴팁 위치 (미지정 시 joyride 기본값) */
  placement?: GuideStep['placement'];
}

/**
 * 페이지별 step 정의.
 * - target: `[data-tour="..."]` (className 변경에 영향받지 않도록 data-tour 사용)
 * - contentKey: `packages/locale/{lng}/guide.json` 의 키
 */
const stepDefs: Record<GuideKey, StepDef[]> = {
  register: [
    { target: '[data-tour="register-email"]', contentKey: 'register_email' },
    { target: '[data-tour="register-send-link"]', contentKey: 'register-send-link' },
    { target: '[data-tour="register-password"]', contentKey: 'register_password' },
    { target: '[data-tour="register-submit"]', contentKey: 'register_submit' },
    { target: '[data-tour="device-configurator"]', contentKey: 'device_configurator' },
  ],
  login: [
    { target: '[data-tour="login-email"]', contentKey: 'login_email' },
    { target: '[data-tour="login-password"]', contentKey: 'login_password' },
    { target: '[data-tour="login-submit"]', contentKey: 'login_submit' },
    { target: '[data-tour="device-configurator"]', contentKey: 'device_configurator' },
  ],
  companyDetail: [
    { target: '[data-tour="company-intro"]', contentKey: 'company_intro' },
    { target: '[data-tour="company-summary"]', contentKey: 'company_summary' },
    { target: '[data-tour="company-info"]', contentKey: 'company_info' },
    { target: '[data-tour="company-info-edit"]', contentKey: 'company_info_edit' },
    { target: '[data-tour="company-manager-info"]', contentKey: 'company_manager_info' },
    { target: '[data-tour="company-manager-edit"]', contentKey: 'company_manager_edit' },
    { target: '[data-tour="site-list"]', contentKey: 'company_site_list' },
    { target: '[data-tour="create-site-button"]', contentKey: 'create_site_button' },
    { target: '[data-tour="site-edit"]', contentKey: 'site_edit' },
    { target: '[data-tour="site-delete"]', contentKey: 'site_delete' },
    { target: '[data-tour="site-show-devices"]', contentKey: 'site_show_devices' },
    { target: '[data-tour="device-reassign"]', contentKey: 'device_reassign' },
    { target: '[data-tour="site-manager-card"]', contentKey: 'site_manager_card' },
    { target: '[data-tour="site-manager-add"]', contentKey: 'site_manager_add' },
    { target: '[data-tour="site-manager-edit"]', contentKey: 'site_manager_edit' },
    { target: '[data-tour="site-manager-delete"]', contentKey: 'site_manager_delete' },
  ],
  siteDetail: [
    { target: '[data-tour="site-summary"]', contentKey: 'site_summary' },
    { target: '[data-tour="site-info"]', contentKey: 'site_info' },
    { target: '[data-tour="site-info-edit"]', contentKey: 'site_info_edit' },
    { target: '[data-tour="site-manager-info"]', contentKey: 'site_manager_info' },
    { target: '[data-tour="site-manager-info-edit"]', contentKey: 'site_manager_info_edit' },
    { target: '[data-tour="device-register"]', contentKey: 'device_register' },
    { target: '[data-tour="registered-device-list"]', contentKey: 'registered_device_list' },
    { target: '[data-tour="device-edit"]', contentKey: 'device_edit' },
    { target: '[data-tour="device-delete"]', contentKey: 'device_delete' },
    { target: '[data-tour="device-detail-expand"]', contentKey: 'device_detail_expand' },
    { target: '[data-tour="device-download-excel"]', contentKey: 'device_download_excel' },
  ],
  companySubscription: [
    { target: '[data-tour="subscription-intro"]', contentKey: 'subscription_intro' },
    {
      target: '[data-tour="subscription-duration-preset"]',
      contentKey: 'subscription_duration_preset',
    },
    { target: '[data-tour="subscription-end-date"]', contentKey: 'subscription_end_date' },
    { target: '[data-tour="subscription-device-limit"]', contentKey: 'subscription_device_limit' },
    { target: '[data-tour="subscription-memo"]', contentKey: 'subscription_memo' },
    { target: '[data-tour="subscription-save"]', contentKey: 'subscription_save' },
  ],
  allowedEmails: [
    { target: '[data-tour="allowedEmails-intro"]', contentKey: 'allowed_emails_intro' },
    { target: '[data-tour="allowed-emails-summary"]', contentKey: 'allowed_emails_summary' },
    { target: '[data-tour="allowed-emails-add"]', contentKey: 'allowed_emails_add' },
    { target: '[data-tour="allowed-emails-list"]', contentKey: 'allowed_emails_list' },
  ],
  allowedEmailInvite: [
    { target: '[data-tour="reg-request-intro"]', contentKey: 'allowed_email_invite_intro' },
    { target: '[data-tour="reg-request-email"]', contentKey: 'allowed_email_invite_email' },
    { target: '[data-tour="reg-request-company"]', contentKey: 'allowed_email_invite_company' },
    {
      target: '[data-tour="reg-request-device-limit"]',
      contentKey: 'allowed_email_invite_device_limit',
    },
    {
      target: '[data-tour="reg-request-duration"]',
      contentKey: 'allowed_email_invite_duration',
    },
    { target: '[data-tour="reg-request-memo"]', contentKey: 'allowed_email_invite_memo' },
  ],
  dashboard: [
    { target: '[data-tour="report-download"]', contentKey: 'report_download', placement: 'bottom' },
    {
      target: '[data-tour="device-status"]',
      contentKey: 'dashboard_device_status',
      placement: 'top',
    },
    { target: '[data-tour="online-summary"]', contentKey: 'online_summary', placement: 'top' },
  ],
  monitoring: [
    { target: '[data-tour="monitoring-intro"]', contentKey: 'monitoring_intro' },
    { target: '[data-tour="monitoring-map"]', contentKey: 'monitoring_map' },
    { target: '[data-tour="monitoring-push"]', contentKey: 'monitoring_push' },
    { target: '[data-tour="monitoring-push-more"]', contentKey: 'monitoring_push_more' },
    { target: '[data-tour="monitoring-device-list"]', contentKey: 'monitoring_device_list' },
    {
      target: '[data-tour="monitoring-device-detail-btn"]',
      contentKey: 'monitoring_device_detail_btn',
    },
    { target: '[data-tour="monitoring-device-detail"]', contentKey: 'monitoring_device_detail' },
  ],
  playback: [
    { target: '[data-tour="playback-timeline"]', contentKey: 'playback_timeline' },
    { target: '[data-tour="playback-device-status"]', contentKey: 'playback_device_status' },
    { target: '[data-tour="playback-map"]', contentKey: 'playback_map' },
    {
      target: '[data-tour="playback-data-trend"]',
      contentKey: 'playback_data_trend',
      placement: 'bottom',
    },
  ],
  datalog: [
    { target: '[data-tour="datalog-import-log-card"]', contentKey: 'datalog_import_log_card' },
    { target: '[data-tour="datalog-import-log-button"]', contentKey: 'datalog_import_log_button' },
    {
      target: '[data-tour="datalog-registered-devices"]',
      contentKey: 'datalog_registered_devices',
    },
    {
      target: '[data-tour="datalog-log-area"]',
      contentKey: 'datalog_log_area',
      placement: 'bottom',
    },
  ],
  configurationLite: [
    {
      target: '[data-tour="config-connect-device"]',
      contentKey: 'config_lite_intro',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-connect-device"]',
      contentKey: 'config_connect_device',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-save-settings"]',
      contentKey: 'config_save_settings',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-save-to-storage"]',
      contentKey: 'config_save_to_storage',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-load-from-storage"]',
      contentKey: 'config_load_from_storage',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-load-default"]',
      contentKey: 'config_load_default',
      placement: 'bottom',
    },
  ],
  configurationMini: [
    {
      target: '[data-tour="config-connect-device"]',
      contentKey: 'config_mini_intro',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-connect-device"]',
      contentKey: 'config_connect_device',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-save-settings"]',
      contentKey: 'config_save_settings',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-save-to-storage"]',
      contentKey: 'config_save_to_storage',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-load-from-storage"]',
      contentKey: 'config_load_from_storage',
      placement: 'bottom',
    },
    {
      target: '[data-tour="config-load-default"]',
      contentKey: 'config_load_default',
      placement: 'bottom',
    },
  ],
};

/** i18n 번역 함수로 step 콘텐츠를 채워 페이지별 step 맵을 만든다. */
export function buildGuideSteps(t: GuideTranslate): Record<GuideKey, GuideStep[]> {
  const result = {} as Record<GuideKey, GuideStep[]>;
  (Object.keys(stepDefs) as GuideKey[]).forEach(key => {
    result[key] = stepDefs[key].map(def => ({
      target: def.target,
      content: t(def.contentKey),
      // 비콘 없이 바로 툴팁을 표시 (react-joyride v3)
      skipBeacon: true,
      ...(def.placement ? { placement: def.placement } : {}),
    }));
  });
  return result;
}

/**
 * 자동 실행 확장용 라우트 → guideKey 매핑.
 * 현재는 수동 실행만 사용하며, 첫 방문 자동 실행을 붙일 때 GuideProvider 에서 활용한다.
 */
export const ROUTE_GUIDE_MAP: Array<{ match: (pathname: string) => boolean; guideKey: GuideKey }> =
  [
    // 실제 data-tour 가 적용되어 가이드가 동작하는 라우트만 등록한다.
    // (브레드크럼 제목 옆 ? 버튼은 이 목록을 기준으로 노출됨)
    { match: pathname => pathname.startsWith('/admin/allowed-emails'), guideKey: 'allowedEmails' },
    { match: pathname => pathname.startsWith('/monitoring/realtime'), guideKey: 'monitoring' },
    { match: pathname => pathname.startsWith('/monitoring/playback'), guideKey: 'playback' },
    { match: pathname => pathname.startsWith('/detaLog'), guideKey: 'datalog' },
    { match: pathname => pathname === '/' || pathname === '', guideKey: 'dashboard' },
    { match: pathname => pathname.startsWith('/configurator/lite'), guideKey: 'configurationLite' },
    { match: pathname => pathname.startsWith('/configurator/mini'), guideKey: 'configurationMini' },
  ];
